import { RadioGroupOptionV2, SelectItemV2, UNIT_DEFAULTS } from '@schenck/rotec-components';
import { SearchKeys } from '../enums/search.enum';
import {
  CompareSettings,
  ProtocolSettings,
  Units,
  Parameters,
  UnitSettings,
} from '../interfaces/settings.interface';
import { Languages } from '../enums/languages.enum';
import { ReportTemplate } from 'src/dto/report-template';

export const unitDefaults: Units = {
  unbalUnit: UNIT_DEFAULTS.unbalance,
  lengthUnit: UNIT_DEFAULTS.length,
  weightUnit: UNIT_DEFAULTS.mass,
  rotationUnit: UNIT_DEFAULTS.frequency,
};

export const compareSettingsDefault: CompareSettings = {
  showAverage: false,
};

export const unitSettingsDefault: UnitSettings = {
  decimals: UNIT_DEFAULTS.decimals,
  units: unitDefaults,
};

export const searchSettingsDefaults = {
  pageSize: 50,
  displayedColumns: [
    SearchKeys.rotorId,
    SearchKeys.typeName,
    SearchKeys.batchId,
    SearchKeys.timestamp,
    SearchKeys.machineName,
    SearchKeys.machineSerial,
    SearchKeys.inTolerance,
  ],
  columnOrder: [
    SearchKeys.rotorId,
    SearchKeys.typeName,
    SearchKeys.operatorName,
    SearchKeys.batchId,
    SearchKeys.projectNo,
    SearchKeys.timestamp,
    SearchKeys.machineName,
    SearchKeys.machineSerial,
    SearchKeys.inTolerance,
  ],
  columnsWidths: {
    [SearchKeys.rotorId]: 250,
    [SearchKeys.typeName]: 250,
    [SearchKeys.operatorName]: 250,
    [SearchKeys.batchId]: 150,
    [SearchKeys.projectNo]: 100,
    [SearchKeys.timestamp]: 200,
    [SearchKeys.machineName]: 200,
    [SearchKeys.machineSerial]: 200,
    [SearchKeys.inTolerance]: 150,
  },
};

export const protocolSettingsDefault: ProtocolSettings = {
  protocolLanguage: 'en-US',
  protocolLogo: '',
  measuringRuns: 'All',
  commentField: true,
  signatureField: true,
  ...unitSettingsDefault,
};

export const defaultParameters: Parameters = {
  header: {
    user: {
      checked: false,
      index: 0,
    },
    date: {
      checked: true,
      index: 1,
    },
    machineId: {
      checked: true,
      index: 2,
    },
    machineName: {
      checked: true,
      index: 3,
    },
    configurationId: {
      checked: false,
      index: 4,
    },
    programId: {
      checked: false,
      index: 5,
    },
    operator: {
      checked: false,
      index: 6,
    },
    customer: {
      checked: false,
      index: 7,
    },
    projectNo: {
      checked: false,
      index: 8,
    },
    orderNo: {
      checked: false,
      index: 9,
    },
  },
  typeData: {
    general: {
      rotorType: {
        checked: true,
        index: 0,
      },
      rotorClass: {
        checked: false,
        index: 1,
      },
    },
    geometry: {
      distanceA: {
        checked: false,
        index: 0,
      },
      distanceB: {
        checked: false,
        index: 1,
      },
      distanceC: {
        checked: false,
        index: 2,
      },
      radius1: {
        checked: false,
        index: 3,
      },
      radius2: {
        checked: false,
        index: 4,
      },
    },
    tolerance: {
      calculationMethod: {
        checked: false,
        index: 0,
      },
      deviation: {
        checked: false,
        index: 1,
      },
      balancingQualityGrade: {
        checked: false,
        index: 2,
      },
      massOfRotor: {
        checked: false,
        index: 3,
      },
      dynamicTolerancePerPlane: {
        checked: false,
        index: 4,
      },
      momentTolerance: {
        checked: false,
        index: 5,
      },
      staticTolerance: {
        checked: false,
        index: 6,
      },
    },
    speed: {
      balancingSpeed: {
        checked: false,
        index: 0,
      },
      serviceSpeed: {
        checked: false,
        index: 1,
      },
      speedWindow: {
        checked: false,
        index: 2,
      },
    },
  },
  result: {
    runs: {
      allRuns: {
        checked: false,
        index: 0,
      },
      firstRun: {
        checked: false,
        index: 1,
      },
      lastRun: {
        checked: true,
        index: 2,
      },
      speed: {
        checked: false,
        index: 3,
      },
      totalNoOfRuns: {
        checked: false,
        index: 4,
      },
    },
    planes: {
      allPlanes: {
        checked: true,
        index: 0,
      },
      plane1: {
        checked: true,
        index: 1,
      },
      plane2: {
        checked: true,
        index: 2,
      },
    },
    unbalance: {
      dynamic: {
        checked: true,
        index: 0,
      },
      moment: {
        checked: false,
        index: 1,
      },
      static: {
        checked: false,
        index: 2,
      },
    },
    correction: {
      method: {
        checked: false,
        index: 0,
      },
      values: {
        checked: false,
        index: 1,
      },
    },
  },
};

export const languageItems: SelectItemV2<Languages>[] = [
  {
    name: 'English',
    value: Languages.en,
  },
  {
    name: 'German',
    value: Languages.de,
  },
  {
    name: 'French',
    value: Languages.fr,
  },
  {
    name: 'Spanish',
    value: Languages.es,
  },
  {
    name: 'Italian',
    value: Languages.it,
  },
  {
    name: 'Dutch',
    value: Languages.nl,
  },
  {
    name: 'Swedish',
    value: Languages.sv,
  },
  {
    name: 'Polish',
    value: Languages.pl,
  },
  {
    name: 'Turkish',
    value: Languages.tr,
  },
  {
    name: 'Chinese',
    value: Languages.zh,
  },
  {
    name: 'Czech',
    value: Languages.cs,
  },
  {
    name: 'Hungarian',
    value: Languages.hu,
  },
  {
    name: 'Korean',
    value: Languages.ko,
  },
  {
    name: 'Portuguese',
    value: Languages.pt,
  },
  {
    name: 'Japanese',
    value: Languages.ja,
  },
];

export const measuringRuns: RadioGroupOptionV2[] = [
  { name: 'All', value: 'All' },
  { name: 'In Tolerance', value: 'InTolerance' },
  { name: 'First', value: 'First' },
  { name: 'Last', value: 'Last' },
  { name: 'First + Last', value: 'FirstLast' },
];

export const defaultTemplate: Partial<ReportTemplate> = {
  config: {
    language: 'en-US',
    lengthUnit: 'mm',
    measuringRun: 'FirstLast',
    numberOfDecimalPlaces: 2,
    unbalUnit: 'g·mm',
    isUnbalanceAsMass: false,
    weightUnit: 'g',
    rotationUnit: unitDefaults.rotationUnit,
  },
  pages: [
    {
      regions: [
        {
          fields: [
            {
              category: 3,
              name: 'configId',
              position: {
                item1: 0,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 3,
              name: 'user',
              position: {
                item1: 1,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 3,
              name: 'orderNo',
              position: {
                item1: 2,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 2,
              name: 'logo',
              position: {
                item1: 0,
                item2: 2,
              },
              style: 0,
              type: 11,
              value: null,
            },
            {
              category: 3,
              name: 'machineType',
              position: {
                item1: 3,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 3,
              name: 'machineId',
              position: {
                item1: 4,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 3,
              name: 'machineName',
              position: {
                item1: 5,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
          ],
          name: '',
          type: 1,
          rows: 6,
        },
        {
          fields: [
            {
              category: 4,
              name: 'rotorType',
              position: {
                item1: 0,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 4,
              name: 'lastChange',
              position: {
                item1: 1,
                item2: 0,
              },
              style: 0,
              type: 10,
              value: null,
            },
            {
              category: 4,
              name: 'setSpeed',
              position: {
                item1: 2,
                item2: 0,
              },
              style: 0,
              type: 7,
              value: null,
            },
            {
              category: 4,
              name: 'distanceA',
              position: {
                item1: 3,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 4,
              name: 'distanceB',
              position: {
                item1: 4,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 4,
              name: 'distanceC',
              position: {
                item1: 5,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 4,
              name: 'radius1',
              position: {
                item1: 6,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 4,
              name: 'radius2',
              position: {
                item1: 7,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 4,
              name: 'tolerance',
              position: {
                item1: 8,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 4,
              name: 'selectedPlanes',
              position: {
                item1: 9,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
          ],
          name: 'Type data',
          type: 2,
          rows: 18,
        },
        {
          fields: [
            {
              category: 5,
              name: 'rotorId',
              position: {
                item1: 0,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 5,
              name: 'speed',
              position: {
                item1: 1,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 5,
              name: 'dynamicUnbalancePlane1',
              position: {
                item1: 2,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 5,
              name: 'dynamicUnbalancePlane2',
              position: {
                item1: 3,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 5,
              name: 'correctionPlane1',
              position: {
                item1: 4,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
            {
              category: 5,
              name: 'correctionPlane2',
              position: {
                item1: 5,
                item2: 0,
              },
              style: 0,
              type: 1,
              value: null,
            },
          ],
          name: '',
          type: 3,
          rows: 12,
        },
        {
          fields: [],
          name: '',
          type: 4,
          rows: 3,
        },
      ],
    },
  ],
  userDefinedFields: [],
};
